import {z} from "zod";

export const MAX_REFERENCE_VEHICLES = 10;

export const permanentParkingPermitStatusBodySchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("suspend"),
    reason: z.string().trim().min(1, "Reason is required"),
  }),
  z.object({
    type: z.literal("revoke"),
    reason: z.string().trim().min(1, "Reason is required"),
  }),
  z.object({
    type: z.literal("reactivate"),
  }),
]);

export const createPermanentParkingTaskSchema = z
  .object({
    property: z.any().refine((v) => v?.id != null, "Property is required"),
    household: z.any().refine((v) => v?.house_hold_id != null, "Household is required"),
    vehicle: z
      .object({
        vehicle_id: z.number(),
        plate: z.string().optional(),
        make: z.string().optional(),
        model: z.string().optional(),
      })
      .nullable()
      .refine((v) => v != null && v.vehicle_id > 0, "Vehicle is required"),
    reference_vehicles: z
      .array(
        z.object({
          vehicle_id: z.number(),
          plate: z.string().optional(),
          make: z.string().optional(),
          model: z.string().optional(),
        }),
      )
      .default([]),
    start_date: z.string().min(1, "Start date is required"),
    end_date: z.string().min(1, "End date is required"),
    is_household_owner: z.union([z.literal(0), z.literal(1)]).optional(),
  })
  .refine((data) => data.end_date >= data.start_date, {
    message: "End date must be on or after start date",
    path: ["end_date"],
  })
  .refine((data) => data.reference_vehicles.length <= MAX_REFERENCE_VEHICLES, {
    message: `You can add up to ${MAX_REFERENCE_VEHICLES} reference vehicles`,
    path: ["reference_vehicles"],
  })
  .refine((data) => {
    const ids = data.reference_vehicles.map((vehicle) => vehicle.vehicle_id);
    return new Set(ids).size === ids.length;
  }, {
    message: "Reference vehicles cannot include duplicates",
    path: ["reference_vehicles"],
  })
  .refine((data) => {
    const primaryId = data.vehicle?.vehicle_id;
    if (!primaryId) return true;
    return !data.reference_vehicles.some((vehicle) => vehicle.vehicle_id === primaryId);
  }, {
    message: "Reference vehicles cannot include the permit vehicle",
    path: ["reference_vehicles"],
  });

export type CreatePermanentParkingTaskSchema = z.infer<typeof createPermanentParkingTaskSchema>;
export type PermanentParkingPermitStatusBody = z.infer<
  typeof permanentParkingPermitStatusBodySchema
>;
